/**
 * Identidad del visitante en MODO PÚBLICO (formulario de pre-chat).
 *
 * En este modo no hay app anfitriona que mintee server-to-server: el propio
 * widget llama a `POST {backendBaseUrl}/api/web/session/public` con la api_key
 * pública del channel NATIVO (con `mint_publico` habilitado) y los datos que el
 * visitante escribió en el formulario. El backend valida el header `Origin`
 * contra los dominios permitidos del tenant.
 *
 * El `externalId` se genera UNA vez por navegador (`pub-<uuid>`, no adivinable)
 * y se persiste en localStorage junto con los datos del formulario, de modo que
 * el visitante recurrente no vuelva a llenarlo y reuse su contacto/chat.
 */

export interface PublicIdentity {
  /** Clave de dedup del contacto en dungo. Generada por el widget, `pub-<uuid>`. */
  externalId: string;
  nombre: string;
  apellido?: string;
  email: string;
  /** Prefijo telefónico con `+`, ej. `+56`. */
  prefijo: string;
  /** Número local, solo dígitos. */
  numero: string;
}

/** Teléfono completo tal como lo espera el mint (`+<prefijo><número>`). */
export function telefonoDe(id: PublicIdentity): string {
  return `${id.prefijo}${id.numero}`;
}

export interface Country {
  code: string;
  prefix: string;
  flag: string;
}

/** Lista curada del selector de prefijo (LatAm + US + ES). */
export const COUNTRIES: Country[] = [
  { code: 'CL', prefix: '+56', flag: '🇨🇱' },
  { code: 'AR', prefix: '+54', flag: '🇦🇷' },
  { code: 'PE', prefix: '+51', flag: '🇵🇪' },
  { code: 'CO', prefix: '+57', flag: '🇨🇴' },
  { code: 'MX', prefix: '+52', flag: '🇲🇽' },
  { code: 'UY', prefix: '+598', flag: '🇺🇾' },
  { code: 'PY', prefix: '+595', flag: '🇵🇾' },
  { code: 'BO', prefix: '+591', flag: '🇧🇴' },
  { code: 'EC', prefix: '+593', flag: '🇪🇨' },
  { code: 'VE', prefix: '+58', flag: '🇻🇪' },
  { code: 'BR', prefix: '+55', flag: '🇧🇷' },
  { code: 'US', prefix: '+1', flag: '🇺🇸' },
  { code: 'ES', prefix: '+34', flag: '🇪🇸' },
];

export const DEFAULT_COUNTRY = 'CL';

const identityKey = (apiKey: string): string => `dungo-widget:identity:${apiKey}`;

/**
 * Lee la identidad guardada para esta api_key. Devuelve null si no hay, si el
 * shape no calza o si el storage está bloqueado (modo privado): en ese caso la
 * identidad dura solo la sesión (el widget la mantiene en memoria).
 */
export function readIdentity(apiKey: string): PublicIdentity | null {
  try {
    const raw = localStorage.getItem(identityKey(apiKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PublicIdentity>;
    if (
      typeof parsed.externalId === 'string' && parsed.externalId &&
      typeof parsed.nombre === 'string' && parsed.nombre &&
      typeof parsed.email === 'string' && parsed.email &&
      typeof parsed.prefijo === 'string' && parsed.prefijo &&
      typeof parsed.numero === 'string' && parsed.numero
    ) {
      return {
        externalId: parsed.externalId,
        nombre: parsed.nombre,
        apellido: typeof parsed.apellido === 'string' ? parsed.apellido : undefined,
        email: parsed.email,
        prefijo: parsed.prefijo,
        numero: parsed.numero,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** Persiste la identidad. Silencioso si el storage no está disponible. */
export function writeIdentity(apiKey: string, identity: PublicIdentity): void {
  try {
    localStorage.setItem(identityKey(apiKey), JSON.stringify(identity));
  } catch {
    /* storage bloqueado: la identidad vive en memoria durante la sesión */
  }
}

/** `pub-<uuid v4>`; con fallback manual si `crypto.randomUUID` no existe. */
export function newExternalId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `pub-${crypto.randomUUID()}`;
    }
  } catch {
    /* cae al fallback */
  }
  const rnd = (len: number): string =>
    Array.from({ length: len }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `pub-${rnd(8)}-${rnd(4)}-4${rnd(3)}-${rnd(4)}-${rnd(12)}`;
}

/**
 * Error del mint público con el `code` del backend cuando lo hay:
 *   - `DOMINIO_NO_PERMITIDO`      el sitio no está en la lista del tenant.
 *   - `MINT_PUBLICO_DESHABILITADO` el channel no tiene el inicio público activo.
 */
export class PublicMintError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(`mint público respondió ${status}${code ? ` (${code})` : ''}`);
    this.name = 'PublicMintError';
  }
}

/** Config pública del widget por channel (estética, no sensible). */
export interface WidgetRemoteConfig {
  /** Color primario configurado en el channel ('#rrggbb') o null. */
  widgetColor: string | null;
}

/**
 * GET {backendBaseUrl}/api/web/widget-config -> config del channel (por api_key).
 * Best-effort: ante cualquier error devuelve null y el widget sigue con sus
 * defaults (el color del host en `theme.primaryColor` siempre tiene prioridad).
 */
export async function fetchWidgetConfig(
  backendBaseUrl: string,
  apiKey: string,
): Promise<WidgetRemoteConfig | null> {
  try {
    const base = backendBaseUrl.replace(/\/+$/, '');
    const res = await fetch(`${base}/api/web/widget-config`, {
      method: 'GET',
      headers: { 'X-Dungo-Api-Key': apiKey },
    });
    if (!res.ok) return null;
    const parsed = (await res.json()) as { widgetColor?: unknown };
    return {
      widgetColor: typeof parsed.widgetColor === 'string' ? parsed.widgetColor : null,
    };
  } catch {
    return null;
  }
}

/** POST {backendBaseUrl}/api/web/session/public -> { token, chatId }. */
export async function mintPublicSession(
  backendBaseUrl: string,
  apiKey: string,
  identity: PublicIdentity,
): Promise<{ token: string; chatId?: string }> {
  const base = backendBaseUrl.replace(/\/+$/, '');
  const body: Record<string, unknown> = {
    externalId: identity.externalId,
    nombre: identity.nombre,
    email: identity.email,
    telefono: telefonoDe(identity),
  };
  if (identity.apellido) body.apellido = identity.apellido;

  const res = await fetch(`${base}/api/web/session/public`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Dungo-Api-Key': apiKey,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let code: string | undefined;
    try {
      const parsed = (await res.json()) as { code?: unknown };
      if (typeof parsed.code === 'string') code = parsed.code;
    } catch {
      /* body no-JSON: reportamos solo el status */
    }
    throw new PublicMintError(res.status, code);
  }

  return (await res.json()) as { token: string; chatId?: string };
}
